import React, { useMemo } from 'react';
import { Page, Document, StyleSheet, Text, View, Image } from '@react-pdf/renderer';
import JsBarcode from 'jsbarcode';
import { useTranslation } from 'react-i18next';
import PdfDocumentViewer from '../../views/PdfDocumentViewer';

const styles = StyleSheet.create({
  page: {
    fontSize: 11,
    paddingTop: 30,
    paddingLeft: 60,
    paddingRight: 60,
    lineHeight: 1.5,
    flexDirection: 'column',
  },
  h1: {
    fontSize: 35,
    textAlign: 'left',
    fontWeight: 'bold',
  },
  barcodeNumber: {
    textAlign: 'center',
  },
  packagesQuantityNumber: {
    fontSize: 50,
    textAlign: 'center',
    marginTop: 20,
  },
  customerInfoContainer: {
    flexDirection: 'row',
  },
  barcodeContainer: {
    height: 180,
  },
  barcodeImage: {
    width: '100%',
    objectFit: 'cover',
  },
  barcodeNumberContainer: {
    height: 80,
  },
  barcodeNumberImage: {
    width: '100%',
    objectFit: 'contain',
  },
});

function generateShipmentBarcodeImages(shipmentId) {
  const canvas1 = document.createElement('canvas');
  JsBarcode(canvas1, shipmentId, {
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
    margin: 0,
    displayValue: false,
    width: 5,
    height: 200,
    fontSize: 40,
  });
  const barcode = canvas1.toDataURL();

  const canvas2 = document.createElement('canvas');
  JsBarcode(canvas2, shipmentId, {
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
    margin: 0,
    displayValue: true,
    width: 0,
    height: 0,
    fontSize: 200,
  });
  const barcodeNumber = canvas2.toDataURL();

  return { barcode, barcodeNumber };
}

function ShipmentStickerPage({ shipment, packageIndex, barcode, barcodeNumber }) {
  return (
    <Page
      key={`${shipment.id}-${packageIndex}`}
      orientation="landscape"
      size={[750, 750]}
      style={styles.page}
    >
      <View>
        <View style={styles.barcodeContainer}>
          <Image style={styles.barcodeImage} src={barcode} />
        </View>
        <View style={styles.barcodeNumberContainer}>
          <Image style={styles.barcodeNumberImage} src={barcodeNumber} />
        </View>
        <Text style={styles.packagesQuantityNumber}>
          {packageIndex + 1}/{shipment.packages_quantity}
        </Text>
        {shipment.owner ? (
          <Text style={styles.h1}>
            {shipment.owner.first_name + ' ' + (shipment.owner.last_name || '')}
          </Text>
        ) : (
          <Text style={styles.h1}>{'No Name'}</Text>
        )}
        {shipment.owner ? <Text style={styles.h1}>{shipment.owner.phone_number}</Text> : null}
        {shipment.shipper_reference ? (
          <Text style={styles.h1}>{shipment.shipper_reference}</Text>
        ) : (
          <Text style={styles.h1}>{'None Shipper Reference'}</Text>
        )}
        {shipment.volumetric_weight ? (
          <Text style={styles.h1}>{shipment.volumetric_weight + ' kg'}</Text>
        ) : (
          <Text style={styles.h1}>{'None Weight/Size'}</Text>
        )}
        {shipment.created_at ? (
          <Text style={styles.h1}>
            {new Date(shipment.created_at).toLocaleString('en-GB', {
              day: 'numeric',
              month: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            })}
          </Text>
        ) : (
          <Text style={styles.h1}>{'No Date'}</Text>
        )}
      </View>
    </Page>
  );
}

function buildShipmentStickerDocument(shipments) {
  return (
    <Document>
      {shipments.map((shipment) => {
        const { barcode, barcodeNumber } = generateShipmentBarcodeImages(shipment.id);
        return Array.from(Array(shipment.packages_quantity).keys()).map((value) => (
          <ShipmentStickerPage
            key={`${shipment.id}-${value}`}
            shipment={shipment}
            packageIndex={value}
            barcode={barcode}
            barcodeNumber={barcodeNumber}
          />
        ));
      })}
    </Document>
  );
}

const ShipmentSticker = ({ shipments }) => {
  const { t } = useTranslation('ui');

  const stickerDocument = useMemo(
    () => (shipments?.length ? buildShipmentStickerDocument(shipments) : null),
    [shipments],
  );

  if (stickerDocument) {
    return (
      <PdfDocumentViewer title={t('shipments.stickersWindowTitle')}>
        {stickerDocument}
      </PdfDocumentViewer>
    );
  }

  return <Text>{t('shipments.noShipmentsForStickers')}</Text>;
};

export default ShipmentSticker;
